import React, {Ref, useEffect} from 'react';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Grid,
    TextField,
} from "@mui/material";
import {
    InventoryBasicSearchParams,
} from "../../model/Inventory";
import DateRangeSelector from "../widgets/DateRangeSelector/DateRangeSelector";
import ItemStatus, { ItemStatusValue } from "../widgets/ItemStatus/ItemStatus";
import ItemType, { ItemTypeValue } from "../widgets/ItemType/ItemType";
import AccountPickerGroup, {
    AccountPickerGroupValue
} from "../widgets/AccountPicker/AccountPickerGroup";
import { DateRange } from "@mui/x-date-pickers-pro";
import { FormikProps } from "formik";

//redux
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { setOptionsBasic, setSearchBasicFormText, setSelectedValuesBasicForm } from '../../features/search/searchSlice';

export interface BasicSearchFormProps {
    searchValues: InventoryBasicSearchParams,
    submitButtonRef?: Ref<HTMLButtonElement>;
    clearButtonRef?: Ref<HTMLButtonElement>;
}

export interface BasicSearchFormParams {
    fullRecord: boolean;
    fullResults: boolean;
    includeLongDesc: boolean;
    includeContent: boolean;
    searchText: string | undefined;
    accountSearchText: string | null;
    type: {
        container: boolean;
        file: boolean;
        tape: boolean;
    };
    status: {
        in: boolean;
        out: boolean;
        destroyed: boolean;
        pending: boolean;
        permout: boolean;
    };
    accounts: {
        level1: string;
        level2: string;
        level3: string;
        subfolders: boolean;
    };

    addDate: DateRange<Date>;
    destroyDate: DateRange<Date>;
    statusDate: DateRange<Date>;
    contentsDate: DateRange<Date>;

    permanent: {
        yes: boolean;
        no: boolean;
    };
}

export default function BasicSearchForm(props: BasicSearchFormProps & FormikProps<BasicSearchFormParams>): JSX.Element {
    const {
        values,
        errors,
        handleChange,
        handleSubmit,
        setFieldValue,
        setValues,
        submitButtonRef,
        clearButtonRef,
    } = props;

    const dispatch = useAppDispatch();
    const selectedValues = useAppSelector((state) => state?.search.selectedValuesBasicForm);

    useEffect(() => {
      if (selectedValues) {
        setFieldValue("accounts.level1", selectedValues.level1);
        setFieldValue("accounts.level2", selectedValues.level2);
        setFieldValue("accounts.level3", selectedValues.level3);
      }
    }, [selectedValues,setFieldValue]);

    const handleTypeChange = (value: ItemTypeValue) => {
        setFieldValue("type", value);
    };

    const handleStatusChange = (value: ItemStatusValue) => {
        setFieldValue("status", value);
    };

    const handleAccountChange = (value: AccountPickerGroupValue) => {
        setFieldValue("accounts.level1", value.level1);
        setFieldValue("accounts.level2", value.level2);
        setFieldValue("accounts.level3", value.level3);
    };

    const handleAccountTextChange = (params: string|null=values.accountSearchText) => {
        setFieldValue("accountSearchText", params);
    };

    const handleAddDateChange = (value: DateRange<Date>) => {
        setFieldValue("addDate", value);
    }

    const handleDestroyDateChange = (value: DateRange<Date>) => {
        setFieldValue("destroyDate", value);
    }

    const handleStatusDateChange = (value: DateRange<Date>) => {
        setFieldValue("statusDate", value);
    }

    const handleContentsDateChange = (value: DateRange<Date>) => {
        setFieldValue("contentsDate", value);
    }

    const clearForm = () => {
        setValues({
            fullRecord: false,
            fullResults: false,
            includeLongDesc: false,
            includeContent: false,
            searchText: "",
            accountSearchText: "",
            type: {
                container: true,
                file: true,
                tape: true,
            },
            status: {
                in: true,
                out: true,
                destroyed: true,
                pending: true,
                permout: true,
            },
            accounts: {
                level1: "",
                level2: "",
                level3: "",
                subfolders: true,
            },
            addDate: [null, null],
            destroyDate: [null, null],
            statusDate: [null, null],
            contentsDate: [null, null],
            permanent: {
                yes: true,
                no: true,
            },
        })
        dispatch(setOptionsBasic(null));
        dispatch(setSearchBasicFormText(null));
        dispatch(setSelectedValuesBasicForm(null));
    }

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <button ref={submitButtonRef} type="submit" style={{display: "none"}}/>
                <button ref={clearButtonRef} type="reset" onClick={clearForm} style={{display: "none"}}/>
                <Grid
                    container
                    sx={{
                        margin: 1,
                        spacing: 1,
                        maxWidth: 1000,
                    }}
                >
                    <Grid item xs={12}>
                        <TextField
                            name="searchText"
                            variant="standard"
                            fullWidth
                            label="Search text"
                            value={values.searchText}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <ItemType value={values.type} onChange={handleTypeChange} />
                    </Grid>
                    <Grid item xs={8}>
                        <ItemStatus value={values.status} onChange={handleStatusChange} />
                    </Grid>
                    <Grid item xs={9}>
                        <AccountPickerGroup
                            type={"basic"}
                            label="Accounts"
                            labelVariant="subtitle1"
                            value={{
                                level1: values.accounts.level1,
                                level2: values.accounts.level2,
                                level3: values.accounts.level3,
                            }}
                            accountSearchText={values.accountSearchText}
                            handleAccountTextChange={handleAccountTextChange}
                            onChange={handleAccountChange}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl component="fieldset" sx={{
                            margin: 1,
                            marginTop: 10,
                            minWidth: 120,
                        }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="accounts.subfolders"
                                        color="secondary"
                                        checked={values.accounts.subfolders}
                                        onChange={handleChange}
                                    />}
                                label="Include Sub Accounts"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <DateRangeSelector
                            error={Boolean(errors.addDate)}
                            helperText={errors.addDate}
                            value={values.addDate}
                            onChange={handleAddDateChange}
                            title="Add date"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DateRangeSelector
                            error={Boolean(errors.destroyDate)}
                            helperText={errors.destroyDate}
                            value={values.destroyDate}
                            onChange={handleDestroyDateChange}
                            title="Destroy date"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DateRangeSelector
                            error={Boolean(errors.statusDate)}
                            helperText={errors.statusDate}
                            value={values.statusDate}
                            onChange={handleStatusDateChange}
                            title="Status date"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DateRangeSelector
                            error={Boolean(errors.contentsDate)}
                            helperText={errors.contentsDate}
                            value={values.contentsDate}
                            onChange={handleContentsDateChange}
                            title="Contents"
                        />
                    </Grid>
                    <Grid item xs={4} sx={{marginTop: 1}}>
                        <FormControl component="fieldset" sx={{ minWidth: 120 }}>
                            <FormLabel component="legend" sx={{ color: "#000000" }}>Permanent</FormLabel>
                            <FormGroup row>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="permanent.yes"
                                            color="secondary"
                                            checked={values.permanent.yes}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Yes"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="permanent.no"
                                            color="secondary"
                                            checked={values.permanent.no}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="No"
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={8} sx={{marginTop: 1}}>
                        <FormControl component="fieldset" sx={{ minWidth: 100 }}>
                            <FormLabel component="legend" sx={{ fontWeight:700, color: "#000000" }}>
                                Extended Search (longer processing time)
                            </FormLabel>
                            <FormGroup row>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="fullResults"
                                            color="secondary"
                                            checked={values.fullResults}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="More than 5000 items"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="includeLongDesc"
                                            color="secondary"
                                            checked={values.includeLongDesc}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Search long description"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="includeContent"
                                            color="secondary"
                                            checked={values.includeContent}
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Search contents"
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
}
